import { SafetyCertificateOutlined } from '@ant-design/icons';
import { Alert, Button, Modal, Space, Spin, Steps, Tag, message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { xhsAccountLoginQrcode, xhsAccountLoginStatus, xhsAccountPersistCookies, xhsMcpEnsure } from '../api';
import { resolveErrorMessage } from '../utils/errorMessage';

const isXhsAccount = (account) => {
  const platform = String(account?.platform || '').trim();
  const platformCode = String(account?.platform_code || '').trim();
  return platformCode === 'xhs' || platform === '小红书' || platform === 'xhs';
};

const parseIsLoggedIn = (resp) => {
  try {
    return Boolean(resp?.login_status?.data?.is_logged_in);
  } catch (_) {
    return false;
  }
};

const getQrImg = (resp) => {
  const img = resp?.qrcode?.data?.img;
  if (!img) return '';
  return String(img);
};

const XhsAccountCheckButton = ({
  account,
  restartMcp = true,
  size = 'small',
  type = 'link',
  label = '检测',
  disabled = false,
  inline = false,
  autoStart = false,
  onSuccess,
  buttonProps = {},
}) => {
  const [checking, setChecking] = useState(false);
  const pollRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [qrImg, setQrImg] = useState('');
  const [statusText, setStatusText] = useState('');
  const [persisting, setPersisting] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [stepIndex, setStepIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const cleanupPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => () => cleanupPolling(), []);
  useEffect(() => {
    if (!autoStart) return;
    // Delay a tick so parent can render modal container
    setTimeout(() => {
      try {
        handleCheck();
      } catch (_) {
        // ignore
      }
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  if (!isXhsAccount(account)) return null;

  const accountId = account?.account_id;
  const accountName = account?.account_name || accountId;

  const closeModal = () => {
    cleanupPolling();
    setOpen(false);
    setQrImg('');
    setStatusText('');
    setPersisting(false);
    setPhase('idle');
    setStepIndex(0);
    setErrorMessage('');
  };

  const refreshQr = async (force = false) => {
    if (!accountId) return;
    try {
      setPhase('fetching');
      setStepIndex(0);
      setErrorMessage('');
      setStatusText(force ? '正在重置并获取二维码...' : '正在获取二维码...');
      const qrResp = await xhsAccountLoginQrcode({ account_id: accountId, force: Boolean(force) });
      if (qrResp?.status !== 'success') {
        const msg = resolveErrorMessage(qrResp?.error_message, '获取二维码失败');
        message.error(msg);
        setPhase('error');
        setErrorMessage(msg);
        return;
      }
      const img = getQrImg(qrResp);
      if (!img) {
        const msg = '获取二维码失败：响应中缺少 img';
        message.error(msg);
        setPhase('error');
        setErrorMessage(msg);
        return;
      }
      setQrImg(img);
      setStatusText('请使用手机扫码登录...');
      setPhase('waiting');
      setStepIndex(1);
    } catch (error) {
      console.error('获取二维码失败:', error);
      const msg = '获取二维码失败';
      message.error(msg);
      setPhase('error');
      setErrorMessage(msg);
    }
  };

  const startPolling = () => {
    cleanupPolling();
    pollRef.current = setInterval(async () => {
      try {
        const s = await xhsAccountLoginStatus({ account_id: accountId, persist: false });
        if (s?.status === 'success' && parseIsLoggedIn(s)) {
          cleanupPolling();
          setStatusText('登录成功，正在同步 cookies...');
          setPhase('persisting');
          setStepIndex(2);
          setPersisting(true);
          try {
            const persistResp = await xhsAccountPersistCookies({ account_id: accountId, restart_mcp: Boolean(restartMcp) });
            if (persistResp?.status === 'success') {
              const restart = persistResp?.restart;
              if (restart?.status === 'failed') {
                const warnReason = resolveErrorMessage(restart?.error, '未知原因');
                const warnMsg = `cookies 已更新，但重启失败：${warnReason}`;
                message.warning(warnMsg);
                setStatusText(warnMsg);
              } else {
                message.success('cookies 已更新并写回配置');
                setStatusText('cookies 已更新并写回配置');
              }
              setPhase('done');
              setStepIndex(3);
              closeModal();
              if (typeof onSuccess === 'function') onSuccess(persistResp);
            } else {
              const fallback = `cookies 同步失败：${persistResp?.status || 'unknown'}`;
              const msg = resolveErrorMessage(persistResp?.error_message || persistResp?.error, fallback);
              message.error(msg);
              setStatusText('登录成功，但 cookies 同步失败（可重试）');
              setPhase('error');
              setErrorMessage(msg);
            }
          } finally {
            setPersisting(false);
          }
        }
      } catch (_) {
        // ignore transient errors while polling
      }
    }, 2500);
  };

  const handleCheck = async () => {
    if (!accountId) {
      message.error('缺少 account_id');
      return;
    }
    setChecking(true);
    setPhase('checking');
    setStepIndex(0);
    setErrorMessage('');
    try {
      const statusResp = await xhsAccountLoginStatus({ account_id: accountId, persist: false });
      if (statusResp?.status === 'success' && parseIsLoggedIn(statusResp)) {
        message.success('账号已登录，可正常发布');
        setStatusText('账号已登录，可正常发布');
        setPhase('done');
        setStepIndex(3);
        return;
      }

      const qrResp = await xhsAccountLoginQrcode({ account_id: accountId, force: false });
      if (qrResp?.status !== 'success') {
        const msg = resolveErrorMessage(qrResp?.error_message, '获取二维码失败');
        message.error(msg);
        setPhase('error');
        setErrorMessage(msg);
        return;
      }

      const img = getQrImg(qrResp);
      if (!img) {
        const msg = '获取二维码失败：响应中缺少 img';
        message.error(msg);
        setPhase('error');
        setErrorMessage(msg);
        return;
      }

      setOpen(true);
      setQrImg(img);
      setStatusText('请使用手机扫码登录...');
      setPhase('waiting');
      setStepIndex(1);
      startPolling();
    } catch (error) {
      console.error('检测登录失败:', error);
      const errText = String(error?.message || error || '').trim();
      // If MCP is not started yet, offer to start it via backend manager then retry.
      const maybeMissingApiBase = errText.includes('missing') && errText.includes('xhs_api_base');
      if (errText.includes('xhs_mcp_unreachable') || maybeMissingApiBase) {
        Modal.confirm({
          title: '小红书 MCP 不可用',
          content: '检测到该账号对应的 MCP 服务不可用/未启动。是否立即启动 MCP 后重试？',
          okText: '启动并重试',
          cancelText: '取消',
          onOk: async () => {
            try {
              const ensureResp = await xhsMcpEnsure({ account_id: accountId });
              if (ensureResp?.status === 'success') {
                message.success('MCP 已启动，正在重新获取二维码...');
                setOpen(true);
                await refreshQr(false);
                startPolling();
                return;
              }
              const msg = resolveErrorMessage(ensureResp?.error_message || ensureResp?.reason, '启动 MCP 失败');
              message.error(msg);
              setPhase('error');
              setErrorMessage(msg);
            } catch (e) {
              const msg = resolveErrorMessage(e?.message || String(e), '启动 MCP 失败');
              message.error(msg);
              setPhase('error');
              setErrorMessage(msg);
            }
          },
        });
      } else {
        const errMsg = resolveErrorMessage(errText);
        const msg = errMsg ? `检测登录失败：${errMsg}` : '检测登录失败，请稍后重试';
        message.error(msg);
        setPhase('error');
        setErrorMessage(msg);
      }
    } finally {
      setChecking(false);
    }
  };

  const phaseMeta = {
    idle: { label: '待开始', color: 'default', spinning: false },
    checking: { label: '检查登录', color: 'blue', spinning: true },
    fetching: { label: '获取二维码', color: 'blue', spinning: true },
    waiting: { label: '等待扫码', color: 'gold', spinning: false },
    persisting: { label: '同步 cookies', color: 'blue', spinning: true },
    done: { label: '已完成', color: 'green', spinning: false },
    error: { label: '出现问题', color: 'red', spinning: false },
  };

  const steps = [
    { title: '获取二维码' },
    { title: '等待扫码' },
    { title: '同步 cookies' },
    { title: '完成' },
  ];

  const progressStatus = phase === 'error' ? 'error' : (phase === 'done' ? 'finish' : 'process');
  const phaseInfo = phaseMeta[phase] || phaseMeta.idle;

  const content = (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Steps size="small" current={stepIndex} status={progressStatus} items={steps} />
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Tag color={phaseInfo.color}>{phaseInfo.label}</Tag>
          {phaseInfo.spinning && <Spin size="small" />}
          <span style={{ color: '#666' }}>{statusText || ' '}</span>
        </div>
        {phase === 'error' && errorMessage && (
          <Alert type="error" showIcon message={errorMessage} style={{ marginTop: 8 }} />
        )}
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{
          width: 260,
          height: 260,
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff',
        }}
        >
          {qrImg ? (
            <img src={qrImg} alt="xhs_login_qrcode" style={{ width: 240, height: 240 }} />
          ) : (
            <div style={{ color: '#999' }}>{open ? '二维码加载中...' : '点击获取二维码'}</div>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#333', marginBottom: 8 }}>扫码后会自动检测登录状态，并同步 cookies。</div>
          {persisting && <div style={{ color: '#999', marginBottom: 8 }}>正在上传 OSS 并回写配置...</div>}
        </div>
      </div>
    </div>
  );

  if (inline) {
    return content;
  }

  return (
    <>
      <Button
        type={type}
        size={size}
        icon={<SafetyCertificateOutlined />}
        onClick={handleCheck}
        loading={checking}
        disabled={disabled || checking || persisting}
        {...buttonProps}
      >
        {label}
      </Button>

      <Modal
        title={`小红书扫码登录：${accountName || '账号'}`}
        open={open}
        onCancel={closeModal}
        footer={(
          <Space wrap>
            <Button onClick={handleCheck} disabled={persisting} loading={checking}>
              获取二维码
            </Button>
            <Button onClick={() => refreshQr(false)} disabled={persisting || !qrImg}>
              刷新二维码
            </Button>
            <Button onClick={() => refreshQr(true)} disabled={persisting}>
              强制重登
            </Button>
            <Button onClick={closeModal}>
              关闭
            </Button>
          </Space>
        )}
      >
        {content}
      </Modal>
    </>
  );
};

export default XhsAccountCheckButton;
